import { z } from 'zod'
import { ShoppingPromotionSchema } from '@reward-platform/shared-schemas'
import { CurrencyCodesSchema } from '@reward-platform/platform-config'
import {
  PaginationSchema,
  PriceLadderSchema,
  PricedPaymentOptionSchema,
  ResponseShoppingFilterSchema,
} from '../../common'
import { CarHirePolicySchema } from '../../common/policies'

export const CarHireSearchOfferSchema = z.object({
  bundleId: z.string(),
  // The recommendationId of the upstream response this car came from. Optional: absent when the
  // availability service made a single upstream call, where the page-level value applies.
  recommendationId: z.string().optional(),
  priceApplicabilityId: z.string(),
  media: z.string().default(''),
  name: z.string(),
  vehicleTransmission: z.string(),
  vehicleClassName: z.string(),
  vendorName: z.string(),
  vendorId: z.string(),
  aircon: z.string().optional(),
  doors: z.string().optional(),
  seats: z.string().optional(),
  bags: z.string().optional(),
  fuel: z.string().optional(),
  cashPrice: z.number(),
  baseAmount: z.number().optional(),
  aviosPrice: z.number(),
  collectAmount: z.number(),
  currencyCode: CurrencyCodesSchema,
  pricingLadder: z.array(PriceLadderSchema).optional(),
  paymentOptions: z.array(PricedPaymentOptionSchema).optional(),
  policies: z.array(CarHirePolicySchema),
  category: z.string().optional(),
  promotions: z.array(ShoppingPromotionSchema).optional(),
  baseLoyalty: z.number().optional(),
  unlimitedMileage: z.string().optional(),
})

export const CarHireSearchDataTransferObjectSchema = z.object({
  // Defaulted rather than required: the availability service's synthetic empty responses (every
  // desk closed, nothing bookable) carry no recommendationId, and nothing reads it off an empty
  // page - the details flow only runs once a car is clicked.
  recommendationId: z.string().default(''),
  correlationId: z.string().min(1),
  results: z.array(CarHireSearchOfferSchema),
  // TODO: pagination should not be optional
  pagination: PaginationSchema.optional(),
  shoppingFilter: ResponseShoppingFilterSchema.optional(),
  // Why an empty result is empty, when the service knows: DESK_CLOSED means every brand's desk is
  // shut at the requested times and no upstream call was made.
  noAvailabilityReason: z.enum(['DESK_CLOSED']).optional(),
  // CAR_HIRE_BRAND_UNAVAILABLE: one brand's upstream call failed and the results cover only the
  // remaining brand - fewer cars than the trip really offers.
  warnings: z
    .array(z.object({ code: z.string().optional(), message: z.string().optional() }))
    .optional(),
})

export type CarHireSearchOffer = z.infer<typeof CarHireSearchOfferSchema>
export type CarHireSearchDataTransferObject = z.infer<typeof CarHireSearchDataTransferObjectSchema>
